import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useRefreshMutation } from "./authApiSlice";
import usePersist from "../../hooks/usePersist";
import { selectCurrentToken, logOut } from "./authSlice";
import Loading from "../../components/spinners/Loading";

// Module-level flag — survives React StrictMode's unmount/remount cycle in dev
// and prevents concurrent refresh calls from multiple renders or effect re-runs.
// Only one /refresh request should ever be in flight at a time.
let refreshInProgress = false;

const PersistLogin = () => {
  const [persist] = usePersist();
  const token = useSelector(selectCurrentToken);
  const dispatch = useDispatch();
  const location = useLocation();
  const [refresh, { isUninitialized, isLoading, isSuccess, isError }] =
    useRefreshMutation();
  const [trueSuccess, setTrueSuccess] = useState(false);

  useEffect(() => {
    const verifyRefreshToken = async () => {
      if (refreshInProgress) return;
      refreshInProgress = true;
      try {
        const response = await refresh();

        if (response?.data?.accessToken) {
          setTrueSuccess(true);
        } else {
          throw new Error("No access token returned from server.");
        }
      } catch (err) {
        console.error("❌ Token refresh failed:", err);
        dispatch(logOut());
      } finally {
        refreshInProgress = false;
      }
    };

    if (!token && persist) {
      verifyRefreshToken();
    }
  }, [token, persist, refresh, dispatch]);

  let content;
  if (!persist) {
    content = <Outlet />;
  } else if (isLoading) {
    content = <Loading />;
  } else if (isError) {
    content = <Navigate to="/login" state={{ from: location }} replace />;
  } else if (isSuccess && trueSuccess) {
    content = <Outlet />;
  } else if (token && isUninitialized) {
    content = <Outlet />;
  } else {
    // persist=true but refresh not yet started (first render) — show spinner
    content = <Loading />;
  }

  return content;
};

export default PersistLogin;
